import { Copy01 } from '@untitledui/icons';
import React, { useCallback, useMemo } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Checkbox } from '@/lib/base/forms/checkbox';
import type { SelectSize } from '@/lib/base/forms/select/select-shared';
import { TimeSelect } from '@/lib/base/forms/time-select';
import { Tooltip } from '@/lib/base/tooltip/tooltip';
import type {
  DayOfWeek,
  WorkingHoursValue,
} from '@/lib/crm/utils/workingHours';
import {
  copyDayToWeekdays,
  DAYS_OF_WEEK,
  parseWorkingHours,
  serializeWorkingHours,
} from '@/lib/crm/utils/workingHours';
import type { TimeFormat } from '@/lib/time/timeFormat';
import { useOrgTimeFormat } from '@/lib/time/useOrgTimeFormat';
import { cx } from '@/utils/cx';

import type { PropertyInputProps } from './TextInput';

interface DayRowProps {
  dayKey: DayOfWeek;
  label: string;
  dayValue: { enabled: boolean; start: string; end: string };
  disabled: boolean;
  timeFormat: TimeFormat;
  /** Field size for the time selects, on the shared library scale. */
  size: SelectSize;
  onToggle: (day: DayOfWeek, enabled: boolean) => void;
  onTimeChange: (day: DayOfWeek, field: 'start' | 'end', time: string) => void;
  onCopyToWeekdays: (day: DayOfWeek) => void;
}

const DayRow: React.FC<DayRowProps> = ({
  dayKey,
  label,
  dayValue,
  disabled,
  timeFormat,
  size,
  onToggle,
  onTimeChange,
  onCopyToWeekdays,
}) => {
  const handleToggle = useCallback(
    (enabled: boolean) => onToggle(dayKey, enabled),
    [dayKey, onToggle]
  );

  const handleStart = useCallback(
    (time: string) => onTimeChange(dayKey, 'start', time),
    [dayKey, onTimeChange]
  );

  const handleEnd = useCallback(
    (time: string) => onTimeChange(dayKey, 'end', time),
    [dayKey, onTimeChange]
  );

  const handleCopyToWeekdays = useCallback(() => {
    onCopyToWeekdays(dayKey);
  }, [dayKey, onCopyToWeekdays]);

  const isAvailable = dayValue.enabled;

  return (
    <div
      className={cx(
        'flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border p-3 transition-colors',
        isAvailable
          ? 'border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)]'
          : 'border-[var(--color-border-primary)] bg-[var(--color-bg-secondary)] opacity-60'
      )}
    >
      <div className='min-w-24 shrink-0'>
        <Checkbox
          isSelected={dayValue.enabled}
          onChange={handleToggle}
          isDisabled={disabled}
          label={
            <span
              className={cx(
                'text-sm font-medium',
                isAvailable
                  ? 'text-[var(--color-text-primary)]'
                  : 'text-[var(--color-text-tertiary)]'
              )}
            >
              {label}
            </span>
          }
        />
      </div>

      {isAvailable && (
        <div className='flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1.5'>
          {/* Keep the time range together as a single shrink-resistant unit so
              the row prefers to drop the Copy button to its own wrapped line
              rather than splitting "09:00 to 17:00" across lines. */}
          <div className='flex flex-nowrap items-center gap-2'>
            <TimeSelect
              size={size}
              format={timeFormat}
              value={dayValue.start}
              isDisabled={disabled}
              onChange={handleStart}
              aria-label={`${label} start time`}
            />
            <span className='text-[var(--color-text-tertiary)]'>to</span>
            <TimeSelect
              size={size}
              format={timeFormat}
              value={dayValue.end}
              isDisabled={disabled}
              onChange={handleEnd}
              aria-label={`${label} end time`}
            />
          </div>
          {/* `ml-auto` pushes the Copy button to the right edge — both when it
              sits on the same line as the time range (visual separation between
              primary inputs and the secondary action) and when it wraps below
              (avoids an orphan icon floating at the left of its own row). */}
          <Tooltip title='Copy to all weekdays' placement='top'>
            <Button
              size='sm'
              color='tertiary'
              iconLeading={Copy01}
              onClick={handleCopyToWeekdays}
              isDisabled={disabled}
              aria-label={`Copy ${label} hours to all weekdays`}
              className='ml-auto'
            />
          </Tooltip>
        </div>
      )}

      {!isAvailable && (
        <span className='text-sm italic text-[var(--color-text-tertiary)]'>
          Unavailable
        </span>
      )}
    </div>
  );
};

export interface WorkingHoursInputProps extends PropertyInputProps {
  ariaLabel?: string;
}

export const WorkingHoursInput: React.FC<WorkingHoursInputProps> = ({
  propertyType,
  value,
  onChange,
  disabled = false,
  ariaLabel,
  size = 'sm',
}) => {
  const parsedValue = useMemo<WorkingHoursValue>(
    () => parseWorkingHours(value),
    [value]
  );

  // Org/user time-format preference — the selects render "9:00 AM" or "09:00"
  // to match every other time label, while stored values stay canonical HH:mm.
  const { timeFormat } = useOrgTimeFormat();

  const handleDayToggle = useCallback(
    (day: DayOfWeek, enabled: boolean) => {
      const next: WorkingHoursValue = {
        ...parsedValue,
        [day]: { ...parsedValue[day], enabled },
      };
      onChange(serializeWorkingHours(next));
    },
    [parsedValue, onChange]
  );

  const handleTimeChange = useCallback(
    (day: DayOfWeek, field: 'start' | 'end', time: string) => {
      const next: WorkingHoursValue = {
        ...parsedValue,
        [day]: { ...parsedValue[day], [field]: time },
      };
      onChange(serializeWorkingHours(next));
    },
    [parsedValue, onChange]
  );

  const handleCopyToWeekdays = useCallback(
    (day: DayOfWeek) => {
      const next = copyDayToWeekdays(parsedValue, day);
      onChange(serializeWorkingHours(next));
    },
    [parsedValue, onChange]
  );

  return (
    <div
      role='group'
      aria-label={ariaLabel ?? propertyType.label ?? 'Working hours'}
      className='space-y-2'
    >
      {DAYS_OF_WEEK.map(({ key, label }) => (
        <DayRow
          key={key}
          dayKey={key}
          label={label}
          dayValue={parsedValue[key]}
          disabled={disabled}
          timeFormat={timeFormat}
          size={size}
          onToggle={handleDayToggle}
          onTimeChange={handleTimeChange}
          onCopyToWeekdays={handleCopyToWeekdays}
        />
      ))}
    </div>
  );
};
